/**
 * The sentence the screen shows when a pasted id opens no record.
 *
 * Three different answers hide behind "nothing opened", and a reader needs to
 * know which one they got:
 *
 * - nothing carries the id at all — a typo, or the wrong environment;
 * - the id is real but it is not a person — a job, a resume, a batch;
 * - the id is a person of the *other* kind. The Users tab searches employees,
 *   so a candidate id pasted there opens nothing, and saying "no such id"
 *   would be false — the record exists, one tab over.
 *
 * The backend is what refuses: it clears the ids a dossier would be opened
 * from when they belong to the subject the tab did not ask for (see
 * `record/search.py: restrict_to_subject`). This only words the refusal.
 */

const SUBJECT_LABEL = {
  candidate: "candidate",
  user: "employee",
};

const TAB_LABEL = {
  candidate: "Candidate",
  user: "User",
};

/**
 * @param {object}  args
 * @param {object?} args.resolved  the search response's `resolved`, or null
 * @param {string}  args.type      the active tab: candidate | user | auto
 * @param {boolean} args.isSuperAdmin  widens "this organization" to the fleet
 * @param {string[]} args.degraded  organizations that could not be reached
 * @returns {string|null} the notice, or null when a record did open
 */
export const resolvedNotice = ({ resolved, type, isSuperAdmin = false, degraded = [] }) => {
  if (!resolved) {
    const missed = degraded.length
      ? ` ${degraded.length} organization${degraded.length === 1 ? "" : "s"} could not be reached, so this is not the whole platform: ${degraded.join(", ")}.`
      : "";
    return (
      (isSuperAdmin
        ? "Nothing in the organizations searched carries that id. Check the id, or search by name, email or phone."
        : "Nothing in this organization carries that id. Check the id, or search by name, email or phone.") + missed
    );
  }

  // A person, of the kind the other tab searches.
  const otherSubject = resolved.kind === "candidate" && type === "user" ? "candidate" : resolved.kind === "user" && type === "candidate" ? "user" : null;

  if (otherSubject) {
    const named = resolved.label ? ` (${resolved.label})` : "";
    return `That id belongs to a ${SUBJECT_LABEL[otherSubject]}${named}, and the ${TAB_LABEL[type]} tab searches ${SUBJECT_LABEL[type]}s. Switch to the ${TAB_LABEL[otherSubject]} tab to open it.`;
  }

  const kindLabel = String(resolved.kind ?? "record").replace(/_/g, " ");
  const label = resolved.label ? ` (${resolved.label})` : "";
  return `That id is a ${kindLabel}${label}. Record Lookup opens candidate and employee records only.`;
};

export default resolvedNotice;
